<script lang="ts">
	import { createEventDispatcher, getContext } from "svelte";

	import Card from "./Card.svelte";
	import CardContainer from "./CardContainer.svelte";
	import Icon from "../lib/Icon.svelte";

	import {
		DataStore,
		getAreaId,
		includesId,
		isSameId,
	} from "@xbs/lib-kanban";
	import type {
		ICard,
		IColumn,
		IRow,
		TEventDispatcher,
		TAreaMeta,
	} from "@xbs/lib-kanban";
	import VirtualScrollColumn from "./columnLayouts/VirtualScrollColumn.svelte";
	import ScrollColumn from "./columnLayouts/ScrollColumn.svelte";

	export let column: IColumn;
	export let areaMeta: TAreaMeta;

	export let row: IRow;
	export let cards: ICard[];

	export let store: DataStore;

	export let cardTemplate = null;

	const _ = getContext<any>("wx-i18n").getGroup("kanban");

	const {
		selected: rSelected,
		cardShape: rCardShape,
		cardsMeta: rCardsMeta,
		scroll: scrollTo,
		readonly: rReadonly,

		before: rOverCardId,
		overAreaId: rOverColId,
		dragItemId: rMovedCardId,
		dragItemsCoords: rDragItemsCoords,
		layout: rLayout,
		cardHeight: rCardHeight,
	} = store.getReactive();

	$: add = $rReadonly.add;
	$: movedCardCoords = $rDragItemsCoords && $rDragItemsCoords[$rMovedCardId];

	$: selected = $rSelected;
	$: cardShape = $rCardShape;
	$: cardsMeta = $rCardsMeta;
	$: overCardId = $rOverCardId;
	$: overColId = $rOverColId;
	$: movedCardId = $rMovedCardId;
	$: layout = $rLayout;
	$: cardHeight = $rCardHeight;

	const dispatch: TEventDispatcher = createEventDispatcher();
	function handleAddCardClick(e: MouseEvent) {
		e.stopPropagation();
		dispatch("action", {
			action: "add-card",
			data: { columnId: column.id, rowId: row.id },
		});
	}

	$: areaId = getAreaId(column.id, row.id);
	$: areaHeight = areaMeta?.height;
	$: areaDragHeight = areaHeight ? `${areaHeight}px` : "auto";

	const borders = 2;
	$: movedCardHeight = (movedCardCoords?.height || 50) - borders;

</script>

<div
	class="wx-column"
	class:wx-collapsed={column.collapsed}
	class:wx-over-limit={areaMeta.isOverLimit}
	data-drop-area={areaId}
	style="min-height:{areaDragHeight};">
	{#if !column.collapsed}
		{#if layout === 'column:lazy'}
			<VirtualScrollColumn
				{column}
				{cards}
				{overCardId}
				{overColId}
				{movedCardId}
				{selected}
				{cardShape}
				{cardTemplate}
				{cardsMeta}
				{movedCardHeight}
				{areaId}
				{scrollTo}
				{cardHeight}
				on:action />
		{:else if layout === 'column:default'}
			<ScrollColumn
				{cards}
				{overCardId}
				{overColId}
				{selected}
				{cardShape}
				{cardTemplate}
				{cardsMeta}
				{movedCardHeight}
				{areaId}
				{cardHeight}
				on:action />
		{:else}
			{#if cards}
				{#each cards as card (card.id)}
					{#if isSameId(card.id, overCardId)}
						<div
							class="wx-drop-area"
							style="min-height:{movedCardHeight}px;" />
					{/if}
					<CardContainer
						cardTemplate={cardTemplate || Card}
						cardFields={card}
						on:action
						dragging={cardsMeta[card.id]?.dragging}
						selected={includesId(selected, card.id)}
						meta={cardsMeta && cardsMeta[card.id]}
						{cardShape}
						{cardHeight} />
				{/each}
			{/if}
			{#if !overCardId && isSameId(overColId, areaId)}
				<div
					class="wx-drop-area"
					style="min-height:{movedCardHeight}px;" />
			{/if}
		{/if}

		<div class="wx-controls-wrapper">
			{#if add && !areaMeta.noFreeSpace}
				<div class="wx-add-card-btn" on:click={handleAddCardClick}>
					<Icon name="wxi-plus" />
					<span class="wx-add-card-tip">
						{_('Add new card')}...
					</span>
				</div>
			{/if}
			{#if areaMeta.rowId && column.limit}
				<div class="wx-swimlane-limit">
					{areaMeta.cardsCount}/{areaMeta.totalLimit}
				</div>
			{/if}
		</div>
	{:else}
		<div class="wx-collapsed-label">
			<div class="wx-label-text">{column.label}</div>
		</div>
	{/if}
</div>

<style>
	.wx-add-card-tip {
		opacity: 0;
		transition: all 0.2s ease-in;
	}
	.wx-column {
		min-width: var(--wx-kanban-column-width);
		width: var(--wx-kanban-column-width);

		margin-left: var(--wx-padding);
		position: relative;
	}
	.wx-column:last-child {
		margin-right: var(--wx-padding);
	}
	.wx-column:hover .wx-add-card-tip {
		opacity: 1;
	}
	.wx-swimlane-limit {
		padding-right: var(--wx-padding);
		background: var(--wx-kanban-background-light);
		padding: 2px var(--wx-padding);
		border-radius: var(--wx-kanban-card-border-radius);
		border: var(--wx-kanban-card-border);
		position: absolute;
		right: 0;
	}
	.wx-controls-wrapper {
		position: relative;
		display: flex;
		align-items: center;
		min-height: 24px;
	}
	.wx-over-limit .wx-swimlane-limit {
		border: solid 1px var(--wx-kanban-over-limit-color);
	}
	.wx-collapsed {
		max-width: var(--wx-kanban-collapsed-column-width);
		width: var(--wx-kanban-collapsed-column-width);
		min-width: 0px;
	}
	.wx-collapsed-label {
		writing-mode: tb-rl;
		font-weight: 500;
		font-size: var(--wx-font-size-md);
		padding-top: var(--wx-padding);

		display: flex;
		align-items: center;
		width: 100%;
		z-index: 5;
	}
	.wx-label-text {
		white-space: nowrap;
		transform: rotate(180deg);
		z-index: 5;
		pointer-events: none;
	}
	.wx-add-card-btn {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		font-size: var(--wx-font-size);
		margin-top: var(--wx-padding);
		margin-bottom: var(--wx-padding);
		cursor: pointer;
	}
	.wx-drop-area {
		width: 100%;
		border-radius: var(--wx-kanban-card-border-radius);
		margin-top: var(--wx-padding);
		border: dotted 1px var(--wx-color-primary);
	}

</style>
